import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export * from './swa-auth';
export * from './recents';
export * from './recents-storage';
export * from './completion-cache';
export * from './login-hint';
export * from './msal-token';
export * from './msal-config';
export * from './msal-app';
export * from './api-client';
export * from './offline';
export * from './feedback-queue';
export * from './feedback-diagnostics';
export * from './app-feedback-url';
export * from './colour';
export * from './outbound-href';

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatNumber(num: number, decimalPlaces = 2): string {
  return new Intl.NumberFormat('en-AU', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(num);
}

export function padNumber(num: number, length: number): string {
  return String(num).padStart(length, '0');
}

export function generateId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

export function isValidPowerAutomateUrl(url: string): boolean {
  if (!isValidUrl(url)) return false;
  const allowedDomains = [
    'logic.azure.com',
    'flow.microsoft.com',
    'azure-apim.net',
    'powerplatform.com',
    'api.powerautomate.com',
  ];
  try {
    const urlObj = new URL(url);
    return allowedDomains.some(domain => urlObj.hostname.includes(domain));
  } catch {
    return false;
  }
}

export function downloadFile(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}
